/**
 * Team zone summary utilities (EPIC-110 D6): dominant attacking lane and
 * readable text/ring colour on a team's kit-primary background.
 */
import type { MatchEvent } from "../../../store/useMatchLogStore";
import {
  computeTeamControlMap,
  type TeamControlLanes,
  type TeamControlThirds,
  type TeamSide,
} from "./teamControlMap";

export type DominantLane = keyof TeamControlLanes;

/** Tie order when lane shares are equal: centre, then left, then right. */
const LANE_TIE_ORDER: DominantLane[] = ["center", "left", "right"];

/** Pick the lane with the largest share; ties prefer centre, left, right. */
export function dominantLane(lanes: TeamControlLanes): DominantLane {
  let best: DominantLane = LANE_TIE_ORDER[0];
  for (const lane of LANE_TIE_ORDER) {
    if (lanes[lane] > lanes[best]) best = lane;
  }
  return best;
}

export interface TeamZoneSummary {
  total: number;
  lane: DominantLane;
  lanePct: number;
  thirds: TeamControlThirds;
}

export function computeTeamZoneSummary(
  events: MatchEvent[],
  teamId: string,
  side: TeamSide,
): TeamZoneSummary {
  const data = computeTeamControlMap(events, teamId, side);
  const lane = dominantLane(data.lanes);
  return {
    total: data.total,
    lane,
    lanePct: data.lanes[lane],
    thirds: data.thirds,
  };
}

/* ------------------------------------------------------------------ */
/*  Contrast                                                           */
/* ------------------------------------------------------------------ */

const FALLBACK_BG = "#475569";
export const STRIP_LIGHT_TEXT = "#ffffff";
export const STRIP_DARK_TEXT = "#0f172a";

function hexToRgb(hex: string): [number, number, number] {
  const m = /^#([0-9A-Fa-f]{6})$/.exec(hex.trim());
  const n = parseInt((m ? m[1] : FALLBACK_BG.slice(1)) as string, 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two 6-digit hex colours. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/** White or slate-900, whichever reaches the higher contrast on `bg`. */
export function readableTextColor(bg: string): string {
  return contrastRatio(bg, STRIP_LIGHT_TEXT) >=
    contrastRatio(bg, STRIP_DARK_TEXT)
    ? STRIP_LIGHT_TEXT
    : STRIP_DARK_TEXT;
}

/** Background actually used (invalid hex falls back to slate-600). */
export function stripBackground(bg: string): string {
  return /^#[0-9A-Fa-f]{6}$/.test(bg.trim()) ? bg.trim() : FALLBACK_BG;
}
